import React, { createContext, useContext, useState, useCallback } from 'react';
import ErrorModal, { ErrorDetails } from '../components/ErrorModal';

interface ErrorContextType {
  showError: (error: ErrorDetails) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errorData, setErrorData] = useState<ErrorDetails | null>(null);
  const [open, setOpen] = useState(false);

  const showError = useCallback((error: ErrorDetails) => {
    console.error('🚨 Global Error Handler:', error);
    
    // Add timestamp if not present
    const errorWithTimestamp = {
      ...error,
      timestamp: error.timestamp || new Date().toISOString(),
    };
    
    setErrorData(errorWithTimestamp);
    setOpen(true);
  }, []);

  const clearError = useCallback(() => {
    setOpen(false);
    // Clear error data after animation completes
    setTimeout(() => setErrorData(null), 300);
  }, []);

  const value = {
    showError,
    clearError,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <ErrorModal
        open={open}
        onClose={clearError}
        errorData={errorData}
      />
    </ErrorContext.Provider>
  );
};

export default ErrorContext;
